import { Link } from "react-router-dom";
import { useProgram, ProgramGate, ProgramLoading, NeedAuth, ProgramShell, PromptList } from "@/pages/programs/_gate";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RenewalNotice } from "@/components/RenewalNotice";

const DISCORD_INVITE = process.env.REACT_APP_DISCORD_INVITE_URL;

function Heading({ children }) {
  return <h2 className="eyebrow mb-4 sm:mb-5 text-accent">{children}</h2>;
}

function Cadence({ cadence }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border/40 mb-12" data-testid="shiftplus-cadence">
      {Object.entries(cadence).map(([day, text]) => (
        <div key={day} className="bg-background p-5 sm:p-6" data-testid={`shiftplus-cadence-${day}`}>
          <div className="font-mono text-[10px] tracking-widest uppercase text-accent mb-2">{day}</div>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{text}</p>
        </div>
      ))}
    </div>
  );
}

function Teachings({ items }) {
  return (
    <div className="space-y-4 mb-12">
      {items.map((t, i) => (
        <article key={i} className="border border-border p-5 sm:p-7" data-testid={`shiftplus-teaching-${i + 1}`}>
          <div className="font-mono text-[10px] tracking-widest text-accent mb-2">PINNED · {String(i + 1).padStart(2, "0")}</div>
          <h3 className="font-serif text-xl sm:text-2xl leading-snug mb-2">{t.title}</h3>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{t.preview}</p>
        </article>
      ))}
    </div>
  );
}

export default function ShiftPlus() {
  const { loading, data, locked, needsAuth } = useProgram("/programs/shift-plus");
  if (needsAuth) return <NeedAuth />;
  if (loading) return <ProgramLoading />;
  if (locked) return <ProgramGate productKey="shift_society" priceLabel="Shift+ · $33/mo" purchaseCta="Upgrade to Shift+" />;
  if (!data) return <ProgramLoading />;

  return (
    <ProgramShell eyebrow="Members Only" title={data.greeting}>
      <RenewalNotice />
      <p className="font-serif italic text-lg sm:text-2xl text-muted-foreground max-w-2xl mb-12 leading-relaxed">{data.intro}</p>

      <Heading>Your weekly rhythm</Heading>
      <Cadence cadence={data.cadence} />

      <Heading>What's included</Heading>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-12">
        {data.features.map((f) => (
          <div key={f.title} className="border border-border p-6 sm:p-8" data-testid={`shiftplus-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-start gap-3 mb-3">
              <Check className="h-4 w-4 text-accent mt-1 shrink-0" />
              <h3 className="font-serif text-xl sm:text-2xl leading-snug">{f.title}</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed pl-7">{f.detail}</p>
          </div>
        ))}
      </div>

      <Heading>Pinned teachings</Heading>
      <Teachings items={data.pinned_teachings} />

      <Heading>Each month</Heading>
      <div className="mb-12" data-testid="shiftplus-calendar"><PromptList items={data.monthly_calendar} numbered /></div>

      <div className="border border-accent bg-accent/5 p-6 sm:p-8">
        <div className="eyebrow mb-3">Next step</div>
        <p className="text-sm sm:text-base text-muted-foreground mb-5 leading-relaxed">{data.next_action}</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer" data-testid="shiftplus-discord-link">
            <Button className="w-full sm:w-auto btn-pill font-mono uppercase text-xs tracking-widest h-12 px-6 gap-2">
              Open the Discord <ArrowRight className="h-4 w-4" />
            </Button>
          </a>
          <Link to="/access" data-testid="shiftplus-access-link">
            <Button variant="outline" className="w-full sm:w-auto btn-pill font-mono uppercase text-xs tracking-widest h-12 px-6">
              My access
            </Button>
          </Link>
        </div>
      </div>
    </ProgramShell>
  );
}
